import { APIClient, useAPIClient } from "@heidelbergmaterials/fcl-apiclient"
import { useEffect, useRef } from "react"

import { correlationIdInterceptor } from "./interceptors/correlation-id"
import { statusCheckInterceptor } from "./interceptors/status-check"

/**
 * Custom hook to get Carbon Bank API client with custom interceptors
 *
 * This hook wraps FCP's useAPIClient and adds Carbon Bank-specific interceptors:
 * - Correlation ID (for analytics tracking)
 * - Status check (for error page routing)
 *
 * @returns APIClient instance with Carbon Bank interceptors configured
 *
 * @example
 * ```tsx
 * import { useCarbonBankClient } from '@shared/api/client/useCarbonBankClient'
 *
 * const MyComponent = () => {
 *   const apiClient = useCarbonBankClient()
 *
 *   const fetchData = async () => {
 *     const response = await apiClient.get('/api/declarations')
 *     return response.data
 *   }
 * }
 * ```
 */
export const useCarbonBankClient = (): APIClient => {
	const { apiClient } = useAPIClient()
	const interceptorsAdded = useRef(false)

	useEffect(() => {
		if (interceptorsAdded.current) return

		// Access internal axios instance to add custom interceptors
		const axiosInstance = (apiClient as any).axiosInstance

		if (!axiosInstance) {
			console.error("Unable to access axios instance from APIClient")
			return
		}

		// Add correlation ID interceptor for analytics
		axiosInstance.interceptors.request.use(
			correlationIdInterceptor,
			(error: unknown) =>
				Promise.reject(
					new Error("Carbon Bank API request interceptor failed", {
						cause: error,
					}),
				),
		)

		// Add status check interceptor for error routing
		axiosInstance.interceptors.response.use(
			(response: any) => response,
			statusCheckInterceptor,
		)

		interceptorsAdded.current = true
	}, [apiClient])

	return apiClient
}
